/**
 * 55 — the now-playing footer.
 *
 * Lives outside #screen and never tears down, so changing route can't interrupt
 * playback or rebuild the transport. Two modes:
 *
 *   condensed — a full-width waveform + the transport + the title. The default,
 *               and everything you need with a thumb.
 *   expanded  — the above, plus a scrolling lyrics strip (the take's synced
 *               words, the active line lit), so you can read along without
 *               opening the song.
 *
 * The waveform spans the whole width, follows the playhead, carries the note
 * dots, and is scrubbable. Transport is prev · −15 · play/pause · +15 · next.
 *
 * It draws its own lightweight canvas rather than a second wavesurfer: the bar
 * envelope is computed once per track from the same peaks JSON the song screen
 * uses, then each frame just repaints bars + playhead — cheap enough to sit in
 * the render loop on a five-year-old phone.
 */

import * as player from './player';
import { getPeaks, getTakeLyrics } from './api';
import { getNow, setNowLyrics, subscribeNow } from './nowdata';
import { h, icon, fmtTime, isLight } from './ui';

const MODE_KEY = '55.nowbar';

const svgPrev = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6L9 12l9 6zM7 6v12" fill="currentColor" stroke="none"/></svg>`;
const svgNext = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l9 6-9 6zM17 6v12" fill="currentColor" stroke="none"/></svg>`;
const svgChevron = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>`;

export interface NowBarOpts {
  openSong: (songId: string) => void;
}

export function mountNowBar(root: HTMLElement, opts: NowBarOpts): void {
  root.replaceChildren();
  root.classList.add('nowbar');

  let mode: 'condensed' | 'expanded' = loadMode();
  root.dataset.mode = mode;

  /* -------------------------------------------------------------- the wave */

  const canvas = h('canvas', { class: 'nb-canvas' }) as HTMLCanvasElement;
  const dots = h('div', { class: 'nb-dots', 'aria-hidden': 'true' });
  const wave = h('div', { class: 'nb-wave', role: 'slider', tabindex: '0', 'aria-label': 'Seek' }, [
    canvas,
    dots,
  ]);

  /* ------------------------------------------------------------ the lyrics */

  const lyrScroll = h('div', { class: 'nb-lyr-scroll' });
  const lyrStrip = h('div', { class: 'nb-lyr', 'aria-hidden': 'false' }, [lyrScroll]);

  /* ---------------------------------------------------------- the transport */

  const prevBtn = h('button', { class: 'nb-tbtn nb-prev', 'aria-label': 'Previous song', html: svgPrev });
  const back15 = h('button', { class: 'nb-skip', 'aria-label': 'Back 15 seconds', text: '−15' });
  const playBtn = h('button', { class: 'nb-play', 'aria-label': 'Play or pause' });
  const fwd15 = h('button', { class: 'nb-skip', 'aria-label': 'Forward 15 seconds', text: '+15' });
  const nextBtn = h('button', { class: 'nb-tbtn nb-next', 'aria-label': 'Next song', html: svgNext });

  const title = h('div', { class: 'nb-title' });
  const sub = h('div', { class: 'nb-sub' });
  const titleBtn = h('button', { class: 'nb-titlebtn', 'aria-label': 'Open the song' }, [title, sub]);

  const expandBtn = h('button', {
    class: 'nb-expand',
    'aria-label': 'Show lyrics',
    'aria-expanded': mode === 'expanded' ? 'true' : 'false',
    html: svgChevron,
  });

  const transport = h('div', { class: 'nb-transport' }, [
    h('div', { class: 'nb-keys' }, [prevBtn, back15, playBtn, fwd15, nextBtn]),
    titleBtn,
    expandBtn,
  ]);

  root.append(wave, lyrStrip, transport);

  /* ----------------------------------------------------------------- wiring */

  titleBtn.addEventListener('click', () => {
    const now = player.get().now;
    if (now) opts.openSong(now.songId);
  });
  playBtn.addEventListener('click', () => player.toggle());
  back15.addEventListener('click', () => skip(-15));
  fwd15.addEventListener('click', () => skip(15));
  prevBtn.addEventListener('click', () => {
    if (player.get().queueLength > 0) player.prev();
    else player.seek(0);
  });
  nextBtn.addEventListener('click', () => {
    if (player.get().queueLength > 0) player.next();
  });
  expandBtn.addEventListener('click', () => setMode(mode === 'expanded' ? 'condensed' : 'expanded'));

  function skip(delta: number): void {
    if (!player.get().now) return;
    player.seek(player.element().currentTime + delta);
  }

  function setMode(m: 'condensed' | 'expanded'): void {
    mode = m;
    root.dataset.mode = m;
    expandBtn.setAttribute('aria-expanded', m === 'expanded' ? 'true' : 'false');
    expandBtn.setAttribute('aria-label', m === 'expanded' ? 'Hide lyrics' : 'Show lyrics');
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      /* private mode — the choice still holds for this session */
    }
    // The lyrics strip only earns its space when it has words; a fresh mode may
    // need the active line re-centred.
    layoutLyrics();
    scrollActive(true);
    drawWave();
  }

  /* -------------------------------------------------------- scrub the wave */

  function seekToClientX(clientX: number): void {
    const d = player.get().duration;
    if (!d || !Number.isFinite(d)) return;
    const r = wave.getBoundingClientRect();
    if (r.width <= 0) return;
    const frac = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    player.seek(frac * d);
  }
  let scrubbing = false;
  wave.addEventListener('pointerdown', (e) => {
    if (!player.get().now) return;
    scrubbing = true;
    try {
      wave.setPointerCapture(e.pointerId);
    } catch {
      /* older Safari — move/up still fire */
    }
    seekToClientX(e.clientX);
    e.preventDefault();
  });
  wave.addEventListener('pointermove', (e) => {
    if (scrubbing) seekToClientX(e.clientX);
  });
  const endScrub = (e: PointerEvent): void => {
    scrubbing = false;
    try {
      wave.releasePointerCapture(e.pointerId);
    } catch {
      /* nothing captured */
    }
  };
  wave.addEventListener('pointerup', endScrub);
  wave.addEventListener('pointercancel', endScrub);
  wave.addEventListener('keydown', (e) => {
    if (!player.get().now) return;
    if (e.key === 'ArrowLeft') {
      skip(-5);
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      skip(5);
      e.preventDefault();
    }
  });

  /* ------------------------------------------------------- per-track state */

  let curVersion: string | null = null;
  let bars: number[] = []; // cached bar envelope for the current track
  let peaksToken = 0; // guards a slow peaks fetch against a newer track
  let lyricEls: HTMLElement[] = [];
  let activeIdx = -1;
  const lyricAbort = { c: null as AbortController | null };

  async function onTrack(versionId: string, takeId: string | null): Promise<void> {
    curVersion = versionId;
    bars = [];
    const token = ++peaksToken;

    // note dots + lyrics come from the store the song screen fills. If it hasn't
    // (album-only playback), fetch this take's lyrics ourselves when we can.
    paintDots();
    paintLyrics();

    // peaks → bar envelope
    try {
      const info = await player.streamInfo(versionId);
      if (token !== peaksToken) return;
      if (info.peaks_url) {
        const peaks = await getPeaks(info.peaks_url);
        if (token !== peaksToken) return;
        if (peaks && peaks[0]) bars = envelope(peaks[0]);
      }
    } catch {
      /* no peaks — the wave falls back to a flat baseline, still scrubbable */
    }
    drawWave();

    // if the store had no lyrics for this version, try to fetch them (bonus)
    if (takeId && getNow(versionId).lyrics.length === 0) {
      lyricAbort.c?.abort();
      const ac = new AbortController();
      lyricAbort.c = ac;
      try {
        const tl = await getTakeLyrics(takeId, ac.signal);
        if (token === peaksToken && tl.lines.length) {
          setNowLyrics(versionId, tl.lines.map((l) => ({ text: l.text, start_s: l.start_s })));
        }
      } catch {
        /* fine — no strip */
      }
    }
  }

  /** Collapse a peaks channel to one amplitude per screen bar. Robust to either
   *  raw samples or interleaved min/max — abs-max captures the envelope either way. */
  function envelope(chan: number[]): number[] {
    const targetBars = Math.max(24, Math.min(600, Math.floor(wave.clientWidth / 3)));
    if (chan.length === 0) return [];
    const per = chan.length / targetBars;
    const out = new Array<number>(targetBars);
    let peak = 0.0001;
    for (let i = 0; i < targetBars; i++) {
      const s = Math.floor(i * per);
      const e = Math.max(s + 1, Math.floor((i + 1) * per));
      let m = 0;
      for (let j = s; j < e && j < chan.length; j++) {
        const a = Math.abs(chan[j] ?? 0);
        if (a > m) m = a;
      }
      out[i] = m;
      if (m > peak) peak = m;
    }
    // normalise to 0..1 so quiet takes still fill the strip
    for (let i = 0; i < out.length; i++) out[i] = (out[i] ?? 0) / peak;
    return out;
  }

  /* --------------------------------------------------------------- drawing */

  function drawWave(): void {
    const w = wave.clientWidth;
    const hgt = wave.clientHeight;
    if (w <= 0 || hgt <= 0) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(hgt * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, hgt);

    const light = isLight();
    const dim = light ? 'rgba(23,23,26,0.22)' : 'rgba(236,230,221,0.20)';
    const accent = '#d8241a';

    const p = player.get();
    const frac = p.duration > 0 ? Math.min(1, p.currentTime / p.duration) : 0;
    const mid = hgt / 2;

    if (bars.length === 0) {
      // no peaks yet — a hairline baseline so the strip reads as a track, and the
      // played portion still shows the playhead progress
      ctx.fillStyle = dim;
      ctx.fillRect(0, mid - 0.5, w, 1);
      ctx.fillStyle = accent;
      ctx.fillRect(0, mid - 0.5, w * frac, 1);
    } else {
      const n = bars.length;
      const bw = w / n;
      const barW = Math.max(1, bw * 0.7);
      for (let i = 0; i < n; i++) {
        const x = i * bw;
        const amp = bars[i] ?? 0;
        const bh = Math.max(1.5, amp * (hgt - 4));
        ctx.fillStyle = x / w <= frac ? accent : dim;
        ctx.fillRect(x + (bw - barW) / 2, mid - bh / 2, barW, bh);
      }
    }

    // the playhead
    const px = Math.round(w * frac);
    ctx.fillStyle = accent;
    ctx.fillRect(px - 1, 0, 2, hgt);
  }

  function paintDots(): void {
    dots.replaceChildren();
    const p = player.get();
    const d = p.duration || 0;
    if (d <= 0) return;
    const marks = getNow(curVersion).marks;
    for (const m of marks) {
      if (m < 0 || m > d) continue;
      const dot = h('span', { class: 'nb-dot' });
      dot.style.left = `${(m / d) * 100}%`;
      dots.appendChild(dot);
    }
  }

  /* --------------------------------------------------------------- lyrics */

  function paintLyrics(): void {
    lyricEls = [];
    activeIdx = -1;
    lyrScroll.replaceChildren();
    const lines = getNow(curVersion).lyrics;
    if (lines.length === 0) {
      lyrStrip.dataset.empty = '1';
      layoutLyrics();
      return;
    }
    lyrStrip.dataset.empty = '0';
    for (const ln of lines) {
      const el = h('button', {
        class: 'nb-lyr-line',
        'data-state': 'up',
        text: ln.text || '♪',
      });
      el.addEventListener('click', () => {
        if (Number.isFinite(ln.start_s)) player.seek(ln.start_s);
      });
      lyricEls.push(el);
      lyrScroll.appendChild(el);
    }
    layoutLyrics();
  }

  function layoutLyrics(): void {
    const hasLyrics = lyricEls.length > 0;
    // strip only takes space in expanded mode AND when there are words
    lyrStrip.hidden = mode !== 'expanded' || !hasLyrics;
  }

  function syncLyrics(t: number): void {
    if (mode !== 'expanded' || lyricEls.length === 0) return;
    const lines = getNow(curVersion).lyrics;
    let idx = -1;
    for (let i = 0; i < lines.length; i++) {
      if ((lines[i]?.start_s ?? 0) <= t + 0.02) idx = i;
      else break;
    }
    if (idx === activeIdx) return;
    activeIdx = idx;
    for (let i = 0; i < lyricEls.length; i++) {
      lyricEls[i]!.dataset.state = i === idx ? 'active' : i < idx ? 'past' : 'up';
    }
    scrollActive(false);
  }

  function scrollActive(instant: boolean): void {
    if (activeIdx < 0 || !lyricEls[activeIdx]) return;
    const el = lyricEls[activeIdx]!;
    const top = el.offsetTop - lyrScroll.clientHeight / 2 + el.clientHeight / 2;
    lyrScroll.scrollTo({ top, behavior: instant ? 'auto' : 'smooth' });
  }

  /* ------------------------------------------------------------- paint loop */

  function paintState(): void {
    const p = player.get();
    const now = p.now;
    root.hidden = !now;
    if (!now) {
      curVersion = null;
      return;
    }

    title.textContent = now.title;
    sub.textContent = p.loading
      ? 'Loading…'
      : p.needsTap
        ? 'Tap play'
        : `${now.subtitle}${p.duration ? ` · ${fmtTime(p.duration)}` : ''}`;

    playBtn.replaceChildren(h('span', { html: p.playing ? icon.pause : icon.play }));
    playBtn.setAttribute('aria-label', p.playing ? 'Pause' : 'Play');

    // next/prev only mean something with a queue (album playback)
    const hasQueue = p.queueLength > 0;
    nextBtn.toggleAttribute('disabled', !hasQueue);
    prevBtn.classList.toggle('is-solo', !hasQueue);

    if (now.versionId !== curVersion) {
      void onTrack(now.versionId, now.takeId ?? null);
    }
  }

  player.subscribe(paintState);
  player.onTick((t) => {
    drawWave();
    syncLyrics(t);
  });

  // the song screen (or our own fetch) filled the store — repaint dots/lyrics
  subscribeNow(() => {
    paintDots();
    // only rebuild the strip if the line set actually changed for this version
    const lines = getNow(curVersion).lyrics;
    if (lines.length !== lyricEls.length) paintLyrics();
  });

  window.addEventListener('resize', () => {
    // width changed → recompute the bar count and redraw
    const p = player.get();
    if (p.now && curVersion) {
      // re-derive envelope at the new width from cached peaks is overkill; a
      // redraw with the existing bars is close enough, and a track change or the
      // next fetch refines it.
      drawWave();
      paintDots();
      scrollActive(true);
    }
  });

  // first paint
  paintState();
}

function loadMode(): 'condensed' | 'expanded' {
  try {
    const v = localStorage.getItem(MODE_KEY);
    if (v === 'expanded' || v === 'condensed') return v;
  } catch {
    /* private mode */
  }
  return 'condensed';
}
